import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Capacitor } from "@capacitor/core";
import { SaorTapToPay } from "saor-tap-to-pay";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CreditCard, Loader2, Smartphone, CheckCircle2, X, Wifi } from "lucide-react";
import PaymentQRCard from "@/components/PaymentQRCard";

/**
 * Driver-side payment card with two modes:
 *
 *  • Native Android (Capacitor APK) with Stripe Terminal SDK installed
 *    → big "Présenter la carte" CTA that triggers Tap-to-Pay via NFC. SAOR
 *      is the merchant of record; the platform automatically transfers 75 %
 *      to the driver's Connect account once the payment succeeds.
 *
 *  • Anywhere else (Safari iPhone, desktop preview, older Android without
 *    the native plugin)
 *    → falls back to the existing QR Stripe Checkout (PaymentQRCard).
 *
 * The mode decision is taken once on mount via Capacitor.getPlatform() and
 * SaorTapToPay.isSupported(). It cannot be toggled by the user — this is a
 * server-side trust boundary.
 */
export default function DriverPaymentCard({ booking, onPaid }) {
  const [supportChecked, setSupportChecked] = useState(false);
  const [tapSupported, setTapSupported] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | initializing | connecting | waiting_for_card | processing | succeeded | failed | cancelled
  const [errorMsg, setErrorMsg] = useState("");
  const [intentId, setIntentId] = useState(null);
  const listenerHandle = useRef(null);

  // Decide once per mount whether to show Tap-to-Pay or the QR fallback.
  useEffect(() => {
    // ⚠️ Stripe Terminal natif Android est désactivé depuis la bascule
    // Square (iteration 72). On force le fallback Square partout :
    // PaymentQRCard ouvre l'app Square POS en deep-link sur iOS et Android.
    setTapSupported(false);
    setSupportChecked(true);
    return;
    // eslint-disable-next-line no-unreachable
    let cancelled = false;
    (async () => {
      try {
        if (Capacitor.getPlatform() !== "android") {
          if (!cancelled) { setTapSupported(false); setSupportChecked(true); }
          return;
        }
        const r = await SaorTapToPay.isSupported();
        if (!cancelled) {
          setTapSupported(!!r.supported);
          setSupportChecked(true);
        }
      } catch (_) {
        if (!cancelled) { setTapSupported(false); setSupportChecked(true); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Subscribe to native payment-status events while this card is mounted.
  useEffect(() => {
    if (!tapSupported) return;
    let handle;
    (async () => {
      handle = await SaorTapToPay.addListener("paymentStatus", (e) => {
        if (e?.status) setStatus(e.status);
        if (e?.message) setErrorMsg(e.message);
      });
      listenerHandle.current = handle;
    })();
    return () => {
      try { handle?.remove?.(); } catch (_) {}
      try { SaorTapToPay.removeAllListeners(); } catch (_) {}
    };
  }, [tapSupported]);

  const startTapToPay = async () => {
    setErrorMsg("");
    try {
      setStatus("initializing");
      const token = localStorage.getItem("saor_token") || "";
      await SaorTapToPay.initialize({
        backendUrl: process.env.REACT_APP_BACKEND_URL,
        authToken: token,
      });

      setStatus("connecting");
      await SaorTapToPay.connectReader();

      // Backend creates the PaymentIntent on the platform account.
      const intentResp = await api.post("/payments/terminal-intent", { booking_id: booking.id });
      const { client_secret, intent_id } = intentResp.data;
      setIntentId(intent_id);

      setStatus("waiting_for_card");
      const r = await SaorTapToPay.collectPayment({
        clientSecret: client_secret,
        intentId: intent_id,
      });

      // Trigger the 75 % transfer to the driver's Connect account.
      // The webhook will do the same — idempotent on the backend.
      await api.post(`/payments/terminal-intent/${r.intentId}/finalize`);
      setStatus("succeeded");
      toast.success(`${booking.price} € encaissés. 75 % transférés.`);
      onPaid?.();
    } catch (e) {
      const msg = e?.message || e?.response?.data?.detail || "Encaissement échoué";
      setErrorMsg(msg);
      setStatus("failed");
      toast.error(msg);
    }
  };

  const cancelTap = async () => {
    try { await SaorTapToPay.cancelCollect(); } catch (_) {}
    setStatus("cancelled");
  };

  // ----- Render

  if (!supportChecked) {
    return (
      <div className="rounded-2xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">
        <Loader2 className="mx-auto h-5 w-5 animate-spin" />
      </div>
    );
  }

  // Web / iPhone / unsupported Android → keep the QR fallback exactly as before.
  if (!tapSupported) {
    return <PaymentQRCard bookingId={booking.id} />;
  }

  // Native Tap-to-Pay UI
  const stepLabel = {
    idle: "Encaissement par carte",
    initializing: "Initialisation du terminal…",
    connecting: "Connexion au lecteur NFC…",
    waiting_for_card: "Présentez la carte du client",
    card_inserted: "Carte détectée",
    processing: "Paiement en cours…",
    succeeded: "Paiement validé",
    failed: errorMsg ? `Échec : ${errorMsg}` : "Échec du paiement",
    cancelled: "Paiement annulé",
  }[status] || status;

  const isBusy = ["initializing", "connecting", "waiting_for_card", "processing"].includes(status);

  return (
    <div
      className="rounded-3xl border-2 border-primary/40 bg-gradient-to-br from-primary/10 via-card to-card p-6 shadow-sm sm:p-7"
      data-testid="driver-tap-to-pay"
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-primary">
        <Smartphone className="h-3.5 w-3.5" />
        Tap to Pay sur Android
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        Encaissez <strong className="text-foreground">{booking.price} €</strong> en présentant la carte du client à l'arrière de votre téléphone (Visa, Mastercard, Apple Pay, Google Pay).
      </p>

      {/* Pulsing NFC indicator while waiting */}
      <div className="mt-6 flex justify-center">
        <div
          className={`relative grid place-items-center rounded-full p-7 ${
            status === "succeeded" ? "bg-emerald-500/10" :
            status === "failed" ? "bg-destructive/10" :
            isBusy ? "bg-primary/15" : "bg-secondary/50"
          }`}
          data-testid="tap-to-pay-target"
        >
          {isBusy && (
            <span className="absolute inset-0 animate-ping rounded-full bg-primary/30" />
          )}
          {status === "succeeded" ? (
            <CheckCircle2 className="h-14 w-14 text-emerald-600" />
          ) : status === "failed" ? (
            <X className="h-14 w-14 text-destructive" />
          ) : status === "waiting_for_card" ? (
            <Wifi className="h-14 w-14 text-primary" />
          ) : isBusy ? (
            <Loader2 className="h-14 w-14 animate-spin text-primary" />
          ) : (
            <CreditCard className="h-14 w-14 text-primary" />
          )}
        </div>
      </div>

      <p className="mt-4 text-center font-[Outfit] text-lg font-medium" data-testid="tap-to-pay-status">
        {stepLabel}
      </p>

      <div className="mt-5 flex flex-wrap justify-center gap-2">
        {(status === "idle" || status === "failed" || status === "cancelled") && (
          <Button
            size="lg"
            className="min-w-[200px] rounded-full"
            onClick={startTapToPay}
            data-testid="tap-to-pay-start"
          >
            <CreditCard className="mr-2 h-4 w-4" />
            {status === "idle" ? "Encaisser maintenant" : "Réessayer"}
          </Button>
        )}
        {isBusy && (
          <Button
            size="lg"
            variant="outline"
            className="rounded-full"
            onClick={cancelTap}
            data-testid="tap-to-pay-cancel"
          >
            <X className="mr-2 h-4 w-4" />
            Annuler
          </Button>
        )}
      </div>

      {/* Tiny ID for support */}
      {intentId && (
        <p className="mt-4 text-center text-[10px] font-mono text-muted-foreground/70" data-testid="tap-to-pay-intent">
          {intentId.slice(0, 24)}…
        </p>
      )}
    </div>
  );
}
